/**
 *@desc 详情右侧
 *@author XinD
 *@date 2022/11/8
 */
import React, { FC, useMemo } from "react";
import { observer } from "mobx-react-lite";
import styles from "../../index.module.scss";
import { ImgAndPromptItemType } from "@/stores/gallery";
import classNames from "classnames";
import { galleryLangRecords, Gallery_Words } from "@/pages/Gallery/lang";
import { parseLabels } from "@/utils/misc";
import { parseModelType } from "@/modules/editor/utils/meta";
import { langStore, translate } from "@noli/business";
import { loginStore } from "@/modules/login/store";
import { postFollowCancel, postFollowCreate } from "@/pages/Gallery/api";
import WithOriginalPrompt from "./WithOriginalPrompt";
import { Box, Flex, Image, Text, Tooltip } from "@chakra-ui/react";
import { getOriginOpChain } from "@/types/meta";
import { THIRD_PARTY_MODEL_TYPES } from "@/thirdParty/constants";
import { Third_Party_Words } from "@/thirdParty/lang";
import { NoliboxModelTypes } from "@/modules/editor/constant";

interface RightContentProps {
  opusId?: number;
  metaJson: any;
  current?: ImgAndPromptItemType;
  copy: (value: string, tips?: string | undefined) => void;
  onFollow?: (user_id: string, follow: boolean) => void;
  onError?: (e: any) => void;
}
interface metaJsonLabelsType {
  // 是否是「AI 生成的图片」
  isAI: boolean;
  // 是否是「文字生成图片」
  isTxt2Img: boolean;
  // 是否是「图片转换」
  isImg2Img: boolean;
  // 是否是「循环图样生成」
  isCircular: boolean;
  // 是否是「动漫图片生成」
  isAnime: boolean;
  // 是否是「超分辨率」
  isSuperResolution: boolean;
  // 是否是「图像修补 inpainting」
  isInpainting: boolean;
  // 是否是「合成风景」
  isLandscapeSynthesis: boolean;
  // 是否是「动漫超分辨率」
  isAnimeSuperResolution: boolean;
  // 是不是百度文心
  isWenxin: boolean;
}
const RightContent: FC<RightContentProps> = ({ ...props }) => {
  const lang = langStore.tgt;
  const { opusId, metaJson, current, copy, onFollow, onError } = props;
  const {
    userState: { userInfo },
  } = loginStore;
  const metaJsonLabels = useMemo<metaJsonLabelsType>(() => {
    const noMetaJson = {
      isAI: false,
      isTxt2Img: false,
      isImg2Img: false,
      isCircular: false,
      isAnime: false,
      isSuperResolution: false,
      isInpainting: false,
      isLandscapeSynthesis: false,
      isAnimeSuperResolution: false,
      isOutpainting: false,
      isScopeReplace: false,
      isWenxin: false,
    };
    return metaJson ? parseLabels(metaJson) : noMetaJson;
  }, [current]);

  const promptTextLabelItemNode = useMemo(() => {
    return Object.keys(metaJsonLabels)
      .map((key, index) => {
        if (key === "model") {
          return null;
        }
        if (key === "version") {
          return null;
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (metaJsonLabels?.[key] && key === "isImg2Img") {
          return (
            // eslint-disable-next-line react/jsx-key, react/no-children-prop
            <Tooltip
              placement="bottom"
              bg="#5f5f5f"
              hasArrow={true}
              label={
                <Box w="100px" h="auto">
                  <Text fontSize="14px">{galleryLangRecords[lang]["originalImage"]}</Text>
                  <Image src={metaJson.data.url ?? ""} w="100%" mt="8px" />
                </Box>
              }
            >
              <li key={`promptTextLabelItemNode${index}`}>{galleryLangRecords[lang][key]}</li>
            </Tooltip>
          );
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (metaJsonLabels?.[key] && key !== "isAI") {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          return <li key={`promptTextLabelItemNode${index}`}>{galleryLangRecords[lang][key]}</li>;
        }
      })
      .filter((it) => !!it);
  }, [metaJsonLabels, metaJson]);
  // 关注/取消关注
  const handleFollow = async (type?: boolean) => {
    try {
      if (type) {
        await postFollowCancel({
          to_user_id: current?.user?.user_id as string,
        });
      } else {
        await postFollowCreate({
          to_user_id: current?.user?.user_id as string,
        });
      }
      onFollow?.(current?.user?.user_id as string, type ? type : false);
    } catch (e) {
      onError?.(e);
    }
  };

  const getModelType = (metaJson: any) => {
    const model = parseModelType(metaJson);
    if (!model) return;
    for (const m of THIRD_PARTY_MODEL_TYPES) {
      if (m === model) {
        return translate(Third_Party_Words[m], lang);
      }
    }

    return galleryLangRecords[lang][`${model as NoliboxModelTypes}ModelTag`];
  };

  const originMetaJson: any = useMemo(() => {
    const opChain = getOriginOpChain(metaJson);
    return opChain[opChain.length - 1];
  }, [metaJson]);
  const isAI = useMemo(() => metaJsonLabels?.isAI || metaJsonLabels?.isWenxin, [metaJsonLabels]);

  const hasOriginNegativePrompt = useMemo(
    () =>
      originMetaJson?.data?.negative_prompt !== originMetaJson?.data?.promptInfo?.originalNegative,
    [originMetaJson?.data?.negative_prompt, originMetaJson?.data?.promptInfo?.originalNegative],
  );

  const hasOriginPrompt = useMemo(
    () => originMetaJson?.data?.text !== originMetaJson?.data?.promptInfo?.original,
    [originMetaJson?.data?.text, originMetaJson?.data?.promptInfo?.original],
  );
  return (
    <div className={styles.rightWrapper}>
      <div
        className={classNames(styles.promptText, {
          [styles.disable]: !isAI && current?.is_open,
        })}
      >
        {isAI ? (
          current?.is_open ? (
            <>
              <WithOriginalPrompt
                title={galleryLangRecords[lang]["prompt"]}
                expandContent={hasOriginPrompt ? null : originMetaJson?.data?.promptInfo?.original}
                needCopy
                copyText={galleryLangRecords[lang]["copy"]}
                expandText={galleryLangRecords[lang]["showOriginal"]}
                content={originMetaJson?.data?.text}
              />
              {originMetaJson?.data?.negative_prompt && (
                <WithOriginalPrompt
                  title={galleryLangRecords[lang]["negativePrompt"]}
                  expandContent={
                    hasOriginNegativePrompt
                      ? null
                      : originMetaJson?.data?.promptInfo?.originalNegative
                  }
                  needCopy
                  copyText={galleryLangRecords[lang]["copy"]}
                  expandText={galleryLangRecords[lang]["showOriginal"]}
                  content={originMetaJson?.data?.negative_prompt}
                />
              )}
            </>
          ) : (
            galleryLangRecords[lang]["The-prompt-is-not-disclosed"]
          )
        ) : (
          galleryLangRecords[lang]["The-image-is-not-generated-by-AI"]
        )}

        {isAI && current?.is_open && (
          <Flex flexDir="column">
            <Flex className={styles.promptTextLabelWrapper}>
              <dt>{galleryLangRecords[lang]["modelType"]}</dt>
              <Box className={styles.labelItem}>
                <li>{getModelType(originMetaJson)}</li>
              </Box>
            </Flex>
            {!!promptTextLabelItemNode.length && (
              <Flex className={styles.promptTextLabelWrapper}>
                <dt>{galleryLangRecords[lang]["handle"]}</dt>
                <Box className={styles.labelItem}>{promptTextLabelItemNode}</Box>
              </Flex>
            )}
            {originMetaJson.data.style && (
              <Flex className={styles.promptTextLabelWrapper}>
                <dt>{galleryLangRecords[lang]["configStyle"]}：</dt>
                <Box className={styles.labelItem}>
                  <li>
                    {galleryLangRecords[lang]["styleText"].replace("$", originMetaJson.data.style)}
                  </li>
                </Box>
              </Flex>
            )}
          </Flex>
        )}
      </div>
      {current?.user?.nickname && current?.user?.avatar && (
        <div className={styles.personalInformation}>
          <a
            href={`/artists/${current?.user?.user_id}`}
            className={styles.avatarMain}
            target="_blank"
            rel="noreferrer"
          >
            <img
              src={`${current?.user?.avatar}?x-oss-process=image/resize,w_48`}
              alt=""
              className={styles.avatar}
            />
            {current?.user?.nickname}
          </a>
          {!opusId && Number(current?.user?.user_id) !== userInfo?.userId && (
            <button
              className={current?.user?.is_follow ? styles.active : ""}
              onClick={async () => {
                if (!loginStore.isLogin) {
                  loginStore.openLoginDialog({}, async () => {
                    await handleFollow(current?.user?.is_follow);
                  });
                } else {
                  await handleFollow(current?.user?.is_follow);
                }
              }}
            >
              {!current?.user?.is_follow
                ? galleryLangRecords[lang]["Follow"]
                : galleryLangRecords[lang]["Followed"]}
            </button>
          )}
        </div>
      )}
      <div className={styles.buttonList}>
        <button
          disabled={!current?.is_open || !isAI}
          onClick={() => {
            copy(originMetaJson?.data?.text, galleryLangRecords[lang]["Copied-to-the-clipboard"]);
          }}
        >
          {galleryLangRecords[lang]["Copy-Prompt"]}
        </button>
        <button
          disabled={!current?.is_open || !isAI}
          onClick={() => {
            copy(current?.meta_json as string, galleryLangRecords[lang]["Copied-to-the-clipboard"]);
          }}
        >
          {galleryLangRecords[lang]["Copy-all-Parameters"]}
        </button>
      </div>
    </div>
  );
};
export default observer(RightContent);
